import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import Logo from "./Logo";
import { useAuth } from "../lib/auth";
import { Button } from "./ui/button";
import { Sheet, SheetContent, SheetTrigger } from "./ui/sheet";
import NotificationBell from "./NotificationBell";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "./ui/dropdown-menu";
import {
  LayoutDashboard,
  Building2,
  Package,
  LogOut,
  Menu,
  ShieldCheck,
  Zap,
  Briefcase,
  Banknote,
  HandCoins,
  ShieldCheck as VerifyIcon,
  MessageCircle,
  BarChart3,
  Settings,
  History,
  Crown,
  BellRing,
  ShieldOff,
  Users as UsersIcon,
} from "lucide-react";

const nav = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, testid: "admin-nav-overview", end: true },
  { to: "/admin/shops", label: "Shops", icon: Building2, testid: "admin-nav-shops" },
  { to: "/admin/reminders", label: "Reminders", icon: BellRing, testid: "admin-nav-reminders" },
  { to: "/admin/distributors", label: "Referral Partners", icon: Briefcase, testid: "admin-nav-distributors" },
  { to: "/admin/staff", label: "Staff", icon: UsersIcon, testid: "admin-nav-staff" },
  { to: "/admin/commissions", label: "Commissions", icon: HandCoins, testid: "admin-nav-commissions" },
  { to: "/admin/payments", label: "Subscriptions", icon: Banknote, testid: "admin-nav-payments" },
  { to: "/admin/verify-payments", label: "Verify Payments", icon: VerifyIcon, testid: "admin-nav-verify" },
  { to: "/admin/verifications", label: "WhatsApp Verifications", icon: VerifyIcon, testid: "admin-nav-verifications" },
  { to: "/admin/whatsapp", label: "WhatsApp", icon: MessageCircle, testid: "admin-nav-whatsapp" },
  { to: "/admin/analytics", label: "Analytics", icon: BarChart3, testid: "admin-nav-analytics" },
  { to: "/admin/plans", label: "Plans", icon: Package, testid: "admin-nav-plans" },
  { to: "/admin/activity", label: "Activity", icon: History, testid: "admin-nav-activity" },
  { to: "/admin/deletions", label: "Deletions", icon: ShieldOff, testid: "admin-nav-deletions" },
  { to: "/admin/referral-settings", label: "Referral Settings", icon: HandCoins, testid: "admin-nav-referral-settings" },
  { to: "/admin/sub-admins", label: "Sub Admins", icon: ShieldCheck, testid: "admin-nav-sub-admins" },
  { to: "/admin/settings", label: "Settings", icon: Settings, testid: "admin-nav-settings" },
];

function SidebarInner({ onNavigate }) {
  return (
    <div className="flex h-full flex-col bg-[#0B0E14] text-slate-100 border-r border-slate-800">
      <div className="flex items-center gap-2 px-5 py-6 border-b border-slate-800">
        <Logo size={36} priority />
        <div>
          <div className="font-display font-bold text-lg tracking-tight">HisaabPro</div>
          <div className="text-[10px] text-amber-400 uppercase font-semibold tracking-wider -mt-0.5">Admin Panel</div>
        </div>
      </div>
      <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1 min-h-0">
        {nav.map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            end={n.end}
            data-testid={n.testid}
            onClick={onNavigate}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
                isActive
                  ? "bg-amber-500/15 text-amber-300 border border-amber-500/30"
                  : "text-slate-300 hover:bg-slate-800 hover:text-white border border-transparent"
              }`
            }
          >
            <n.icon className="w-[18px] h-[18px]" />
            {n.label}
          </NavLink>
        ))}
      </nav>
      <div className="px-5 py-4 text-[11px] text-slate-500 border-t border-slate-800">
        Control center · HisaabPro
      </div>
    </div>
  );
}

export default function AdminShell({ children }) {
  const { user, logout } = useAuth();
  const [mobileOpen, setMobileOpen] = React.useState(false);

  return (
    <div className="min-h-screen bg-[#0F1319] text-slate-200 flex">
      <aside className="hidden lg:flex w-[250px] shrink-0 fixed inset-y-0 left-0 h-screen overflow-hidden">
        <SidebarInner />
      </aside>

      <div className="flex-1 lg:ml-[250px] min-w-0">
        <header className="sticky top-0 z-30 bg-[#0B0E14] border-b border-slate-800 h-16 flex items-center justify-between px-4 sm:px-6">
          <div className="flex items-center gap-2">
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="lg:hidden text-slate-200" data-testid="admin-open-sidebar">
                  <Menu className="w-5 h-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="p-0 w-[260px] bg-[#0B0E14] border-slate-800">
                <SidebarInner onNavigate={() => setMobileOpen(false)} />
              </SheetContent>
            </Sheet>
            <div>
              <div className="text-[10px] text-amber-400 font-semibold uppercase tracking-wider">Platform</div>
              <div className="text-sm font-display font-semibold">Control Center</div>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <span
              data-testid="admin-role-badge"
              className="hidden sm:inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold bg-amber-500/15 text-amber-300 border border-amber-500/30"
            >
              <ShieldCheck className="w-3 h-3" /> Platform Admin
            </span>
            <NotificationBell />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="admin-user-menu-btn"
                  className="flex items-center gap-3 rounded-md px-2 py-1.5 hover:bg-slate-800 transition"
                >
                  <div className="w-9 h-9 rounded-full bg-amber-500 text-slate-900 flex items-center justify-center font-bold">
                    {(user?.owner_name || user?.email || "A").charAt(0).toUpperCase()}
                  </div>
                  <div className="hidden sm:block text-left">
                    <div className="text-sm font-semibold leading-tight">{user?.owner_name}</div>
                    <div className="text-xs text-slate-500">{user?.email}</div>
                  </div>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>Platform Admin</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={logout} data-testid="admin-logout-btn">
                  <LogOut className="w-4 h-4 mr-2" /> Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <main className="p-4 sm:p-6 lg:p-8 max-w-[1400px] mx-auto">{children}</main>
      </div>
    </div>
  );
}
